"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Layers, CalendarDays, BadgePercent, ShieldCheck, ArrowRight, Phone, CircleCheck as CheckCircle2, HardHat, FileSpreadsheet, Wrench, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function SolarBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = [{
    icon: Clock,
    title: "Reliable turnaround scheduling",
    description: "Erection and strike dates planned in advance to keep your solar PV crews working on schedule."
  }, {
    icon: Layers,
    title: "Multi-property rollout capability",
    description: "Dedicated transport and scaffold gangs equipped to handle high-volume residential retrofit schemes and social housing programmes across the South East."
  }, {
    icon: CalendarDays,
    title: "Weekend installation support",
    description: "Flexible site access setups allowing your teams to mount panels and complete inverters over Saturdays and Sundays when properties are accessible."
  }, {
    icon: BadgePercent,
    title: "Structured trade pricing",
    description: "Predictable, tiered contractor rates for single roofs and multi-plot developments with transparent weekly hire rates and no surprise strike fees."
  }];
  const coordinationSteps = [{
    step: "01",
    icon: FileSpreadsheet,
    title: "Schedule & load planning",
    text: "You share your PV rollout dates. We verify roof eaves heights, gutter clearances, and designate panel loading zones on the deck."
  }, {
    step: "02",
    icon: HardHat,
    title: "Compliant erection",
    text: "Our qualified scaffolders build safe roof-edge handrails, hop-up platforms, and hoist points strictly aligned with NASC TG20 standards."
  }, {
    step: "03",
    icon: Wrench,
    title: "Installer handover & green tag",
    text: "We sign off the handover certificate, hand the site to your electrical crew, and remain on call for any platform adjustments."
  }, {
    step: "04",
    icon: CheckCircle2,
    title: "Priority strike & clear site",
    text: "Once panels and commissioning tests are complete, we strike the framework promptly to maintain your customer satisfaction."
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.075,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-solar-installers-choose-bmf" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 border-b border-border overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-accent text-accent-foreground text-xs font-bold uppercase tracking-wider mb-4 border border-border">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Solar Contractor Partnerships
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default leading-tight">
            Scaffolding engineered around your solar installation timeline.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            PV mounting teams need precise edge protection, clear panel lift access, and scaffolds that come down promptly when the job is done. We deliver dependable operational support across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.
          </p>
        </div>

        {/* Benefits Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {benefits.map((benefit, index) => {
          const IconComponent = benefit.icon;
          return <motion.div key={benefit.title} variants={itemVariants} data-index={index} className="h-full">
                <Card className="h-full rounded-lg border border-border bg-card text-card-foreground shadow-sm hover:border-primary/50 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 rounded-md bg-accent flex items-center justify-center text-primary mb-4 border border-border">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <CardTitle className="text-xl font-bold font-default tracking-tight text-card-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed font-sans">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Operational Timeline Block */}
        <div className="bg-secondary text-secondary-foreground rounded-xl p-8 sm:p-10 md:p-12 border border-border/20 shadow-xl relative overflow-hidden">
          <div className="absolute -right-16 -top-16 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="max-w-2xl mb-12">
            <div className="flex items-center gap-2 text-primary text-xs font-bold tracking-widest uppercase mb-2">
              <ShieldCheck className="w-4 h-4" />
              Coordination Protocol
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold font-default tracking-tight text-secondary-foreground">
              How we work alongside your installation crews
            </h3>
            <p className="text-secondary-foreground/80 mt-2 text-sm sm:text-base font-sans">
              A structured four-step process ensuring zero site hold-ups, compliant handover paperwork, and direct scaffolder-to-electrician coordination.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {coordinationSteps.map((step, index) => {
            const StepIcon = step.icon;
            return <motion.div key={step.step} variants={itemVariants} data-index={index} className="relative flex flex-col justify-between bg-card/5 backdrop-blur-sm rounded-lg p-6 border border-white/10 hover:border-primary/40 transition-[border-color] duration-200">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-black font-mono text-primary">
                        {step.step}
                      </span>
                      <div className="w-9 h-9 rounded-md bg-secondary-foreground/10 flex items-center justify-center text-primary">
                        <StepIcon className="w-5 h-5" />
                      </div>
                    </div>
                    <h4 className="text-lg font-bold font-default text-secondary-foreground mb-2">
                      {step.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-secondary-foreground/80 font-sans leading-relaxed">
                      {step.text}
                    </p>
                  </div>
                </motion.div>;
          })}
          </motion.div>

          {/* Bottom Action Strip */}
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3 text-secondary-foreground/90 text-sm font-sans">
              <Sparkles className="w-5 h-5 text-primary shrink-0" />
              <span>NASC &amp; CHAS accredited installation access.</span>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
              <Button asChild variant="default" size="lg" className="w-full sm:w-auto font-semibold">
                <Link to="/ContactRequestQuotePage">
                  Discuss a solar rollout
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent border-white/30 text-secondary-foreground hover:bg-white/10 hover:text-secondary-foreground">
                <a href="tel:01344929900">
                  <Phone className="w-4 h-4 mr-1" />
                  Call trade desk
                </a>
              </Button>
            </div>
          </div>

        </div>

      </div>
    </section>;
}